import { useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { makePatchApiCall } from "../../shared/makeApiCall";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";

export default function ExpensesListViewModel() {
  // follow paginator shape: { data: [], total: 0, current_page: 1 }
  const [expenses, setExpenses] = useState({
    data: [],
    total: 0,
    current_page: 1,
  });
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ startDate: null, endDate: null });

  async function loadExpenses(page = 1, search = null, filters = {}) {
    setLoading(true);
    // Build query string with optional filters
    const qs = new URLSearchParams();
    qs.set("page", page);
    if (search) qs.set("vendor", search);
    if (filters.startDate) qs.set("start_date", filters.startDate);
    if (filters.endDate) qs.set("end_date", filters.endDate);
    if (filters.per_page) qs.set("per_page", filters.per_page);

    const res = await makeGetApiCall(`expenses?${qs.toString()}`);
    if (res.success) {
      // If Laravel paginator, use as-is; if an array, wrap it
      if (res.data && Array.isArray(res.data.data)) {
        setExpenses(res.data);
      } else if (Array.isArray(res.data)) {
        setExpenses({
          data: res.data,
          total: res.data.length,
          current_page: 1,
        });
      } else {
        // fallback
        setExpenses({ data: [], total: 0, current_page: 1 });
      }
    }
    setLoading(false);
  }

  const resetFilters = () => {
    const f = { startDate: null, endDate: null };
    setFilters(f);
    setShowFilters(false);
  };

  const filterComponents = [
    <DateRangeFilter
      endDate={filters.endDate}
      onChange={(name, value) => setFilters({ ...filters, [name]: value })}
      resetFilter={() => setFilters({ startDate: null, endDate: null })}
      startDate={filters.startDate}
    />,
  ];

  async function uploadExpense(formData) {
    // makePostApiCall expects JSON; for multipart we'll use axios directly
    const token = localStorage.getItem("authToken");
    try {
      const axios = (await import("../../axios")).axiosApiInstance;
      const response = await axios.post("expenses/upload", formData, {
        headers: {
          Authorization: "Bearer " + token,
          "content-type": "multipart/form-data",
        },
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, data: err.response ? err.response.data : {} };
    }
  }

  async function manualCreate(payloadOrFormData) {
    // If payloadOrFormData is a FormData (file included), send multipart via axios
    if (payloadOrFormData instanceof FormData) {
      const token = localStorage.getItem("authToken");
      try {
        const axios = (await import("../../axios")).axiosApiInstance;
        const response = await axios.post(
          "expenses/manual-upload",
          payloadOrFormData,
          {
            headers: {
              Authorization: "Bearer " + token,
              "content-type": "multipart/form-data",
            },
          }
        );
        return { success: true, data: response.data };
      } catch (err) {
        return { success: false, data: err.response ? err.response.data : {} };
      }
    }

    // Otherwise assume JSON payload and use makePostApiCall
    return await makePostApiCall("expenses/manual-upload", payloadOrFormData);
  }

  async function updateExpense(expenseId, payload) {
    // payload should contain only allowed editable fields per API: vendor, vendor_address, total_amount, tax_amount, date
    try {
      const res = await makePatchApiCall(
        `expenses/update/${expenseId}`,
        payload
      );
      return res;
    } catch (err) {
      return { success: false, data: err.response ? err.response.data : {} };
    }
  }

  async function deleteExpense(expenseId) {
    try {
      const token = localStorage.getItem("authToken");
      const axios = (await import("../../axios")).axiosApiInstance;
      const r = await axios.delete(`expenses/${expenseId}`, {
        headers: { Authorization: "Bearer " + token },
      });
      return { success: true, data: r.data };
    } catch (err) {
      return { success: false, data: err.response ? err.response.data : {} };
    }
  }

  return {
    expenses,
    loadExpenses,
    uploadExpense,
    manualCreate,
    showUpload,
    setShowUpload,
    setExpenses,
    loading,
    updateExpense,
    // filter state and helpers
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    resetFilters,
    filterComponents,
    deleteExpense,
  };
}
